import { z } from 'zod';
import {
  ApprovalStatus,
  AssetType,
  DateOnly,
  HexColor,
  Id,
  RightsLicense,
  Timestamp,
  UnitInterval,
  Uri,
} from './common.js';
import { AssetCard } from './assets.js';
import { NormalizedBox, TranscriptWord } from './media.js';

export const SEARCH_DEFAULT_LIMIT = 60;
export const SEARCH_MAX_LIMIT = 200;

export const ColorFilter = z.object({
  hex: HexColor,
  /** CIE ΔE-based distance normalized 0–1 against dominant_colors. */
  tolerance: UnitInterval.default(0.15),
});
export type ColorFilter = z.infer<typeof ColorFilter>;

export const CustomFieldFilterOp = z.enum(['eq', 'in', 'contains', 'gte', 'lte', 'between']);
export type CustomFieldFilterOp = z.infer<typeof CustomFieldFilterOp>;

export const CustomFieldFilter = z.object({
  field_id: Id,
  op: CustomFieldFilterOp,
  value: z.union([z.string(), z.number(), z.boolean(), z.array(z.union([z.string(), z.number()]))]),
});
export type CustomFieldFilter = z.infer<typeof CustomFieldFilter>;

export const DateFilter = z.object({
  field: z.string().regex(/^(created_at|uploaded_at|custom:[0-9a-fA-F-]{36})$/),
  from: DateOnly.nullable().optional(),
  to: DateOnly.nullable().optional(),
});
export type DateFilter = z.infer<typeof DateFilter>;

export const Orientation = z.enum(['landscape', 'portrait', 'square']);

/** Release 2 C4 `SearchFilters.rights`. Every given key must hold. */
export const RightsFilter = z.object({
  /** true = `expires_at` <= now; false = no expiry or in the future. */
  expired: z.boolean().optional(),
  /** now < `expires_at` <= now + N days. */
  expiring_within_days: z.int().min(1).max(3650).optional(),
  has_expiry: z.boolean().optional(),
  /** release4/R (META-3): true = embargoed (`valid_from` > now); false = no `valid_from` or already valid. */
  not_yet_valid: z.boolean().optional(),
  /** Any of these licenses; empty = no constraint. */
  licenses: z.array(RightsLicense).optional(),
});
export type RightsFilter = z.infer<typeof RightsFilter>;
export type Orientation = z.infer<typeof Orientation>;

/** Structured facets (05 §2). All filters are ANDed. */
export const SearchFilters = z.object({
  board_ids: z.array(Id).optional(),
  include_descendants: z.boolean().default(true),
  types: z.array(AssetType).optional(),
  orientation: z.array(Orientation).optional(),
  colors: z.array(ColorFilter).max(5).optional(),
  tag_ids: z.array(Id).optional(),
  tag_match: z.enum(['all', 'any']).default('any'),
  /** person_cluster_id values. */
  people: z.array(Id).optional(),
  /** Detected object labels. */
  objects: z.array(z.string()).optional(),
  custom_fields: z.array(CustomFieldFilter).optional(),
  approval: z.array(ApprovalStatus).optional(),
  date: DateFilter.optional(),
  duration_ms: z
    .object({ min: z.int().min(0).optional(), max: z.int().min(0).optional() })
    .optional(),
  min_width: z.int().min(0).optional(),
  min_height: z.int().min(0).optional(),
  has_transcript: z.boolean().optional(),
  uploaded_by: z.array(Id).optional(),
  include_deleted: z.boolean().default(false),
  /** Release 1 S7a: include archived assets and boards (optional so existing filter literals stay valid). */
  include_archived: z.boolean().optional(),
  // --- release2 (contract) --- frozen predicates (docs/specs/release2-contract.md "Predicates"); all optional
  /** S4 (lane A): true = no `source=user` tag; false = at least one. */
  untagged: z.boolean().optional(),
  /** S4 (lane A): true = in no live board below a library's top level other than one named "Inbox". */
  unfiled: z.boolean().optional(),
  /** G3 (lane B): true = a required custom field has no value. */
  incomplete: z.boolean().optional(),
  /** C4 (lane C): every given key must hold. */
  rights: RightsFilter.optional(),
});
export type SearchFilters = z.infer<typeof SearchFilters>;

export const SearchMode = z.enum(['hybrid', 'semantic', 'keyword']);
export type SearchMode = z.infer<typeof SearchMode>;

export const SearchSort = z.enum([
  'relevance',
  'created_at:desc',
  'created_at:asc',
  'name:asc',
  'name:desc',
  'size:desc',
  'size:asc',
]);
export type SearchSort = z.infer<typeof SearchSort>;

export const SearchFacetName = z.enum([
  'types',
  'orientation',
  'approval',
  'tags',
  'colors',
  'boards',
  'people',
  'objects',
  'uploaded_by',
  'custom_fields',
  // release2: tidy (untagged / unfiled / incomplete) and rights (none / valid / expiring / expired)
  'tidy',
  'rights',
]);
export type SearchFacetName = z.infer<typeof SearchFacetName>;

export const SearchRequest = z.object({
  workspace_id: Id,
  q: z.string().max(1000).nullable().optional(),
  mode: SearchMode.default('hybrid'),
  filters: SearchFilters.optional(),
  sort: SearchSort.default('relevance'),
  cursor: z.string().nullable().optional(),
  limit: z.int().min(1).max(SEARCH_MAX_LIMIT).default(SEARCH_DEFAULT_LIMIT),
  facets: z.array(SearchFacetName).optional(),
  highlight: z.boolean().default(true),
});
export type SearchRequest = z.infer<typeof SearchRequest>;
/** Input shape of SearchRequest (defaults optional) — what clients build. */
export type SearchRequestInput = z.input<typeof SearchRequest>;

export const FacetBucket = z.object({
  value: z.string(),
  label: z.string().optional(),
  count: z.int(),
});
export type FacetBucket = z.infer<typeof FacetBucket>;

export const ColorFacetBucket = z.object({
  hex: z.string(),
  count: z.int(),
});
export type ColorFacetBucket = z.infer<typeof ColorFacetBucket>;

export const Facets = z.object({
  types: z.array(FacetBucket).optional(),
  orientation: z.array(FacetBucket).optional(),
  approval: z.array(FacetBucket).optional(),
  tags: z.array(FacetBucket).optional(),
  colors: z.array(ColorFacetBucket).optional(),
  boards: z.array(FacetBucket).optional(),
  people: z.array(FacetBucket).optional(),
  objects: z.array(FacetBucket).optional(),
  uploaded_by: z.array(FacetBucket).optional(),
  /** Release 2: `untagged` / `unfiled` / `incomplete` (not exclusive; zero buckets omitted). */
  tidy: z.array(FacetBucket).optional(),
  /** Release 2: `none` / `valid` / `expiring` / `expired`. */
  rights: z.array(FacetBucket).optional(),
  /** Keyed by field_id. */
  custom_fields: z.record(z.string(), z.array(FacetBucket)).optional(),
});
export type Facets = z.infer<typeof Facets>;

export const AppliedHintKind = z.enum([
  'extension',
  'orientation',
  'color',
  'tag',
  'board',
  'approval',
  'before',
  'after',
  'type',
]);
export type AppliedHintKind = z.infer<typeof AppliedHintKind>;

export const AppliedHint = z.object({
  kind: AppliedHintKind,
  value: z.string(),
  source: z.literal('q'),
  raw: z.string().optional(),
});
export type AppliedHint = z.infer<typeof AppliedHint>;

export const MatchRegion = NormalizedBox.extend({
  label: z.string(),
  confidence: UnitInterval,
  frame_t_ms: z.int().nullable().optional(),
});
export type MatchRegion = z.infer<typeof MatchRegion>;

export const FrameHit = z.object({
  t_ms: z.int(),
  score: z.number(),
  thumbnail_url: Uri.nullable().optional(),
});
export type FrameHit = z.infer<typeof FrameHit>;

export const TranscriptHit = z.object({
  text: z.string(),
  start_ms: z.int(),
  end_ms: z.int(),
  words: z.array(TranscriptWord),
});
export type TranscriptHit = z.infer<typeof TranscriptHit>;

export const TextHitField = z.enum([
  'title',
  'description',
  'filename',
  'tags',
  'custom_field',
  'ocr_text',
  'transcript',
]);
export type TextHitField = z.infer<typeof TextHitField>;

export const TextHit = z.object({
  field: TextHitField,
  snippet: z.string(),
  field_id: Id.nullable().optional(),
});
export type TextHit = z.infer<typeof TextHit>;

/** Explanation of why the asset matched (05 §4). */
export const SearchMatch = z.object({
  semantic_score: z.number().nullable().optional(),
  keyword_score: z.number().nullable().optional(),
  regions: z.array(MatchRegion).optional(),
  frames: z.array(FrameHit).optional(),
  transcript_hits: z.array(TranscriptHit).optional(),
  text_hits: z.array(TextHit).optional(),
  tags: z.array(z.string()).optional(),
  colors: z.array(z.string()).optional(),
});
export type SearchMatch = z.infer<typeof SearchMatch>;

export const SearchResult = z.object({
  asset: AssetCard,
  score: z.number(),
  match: SearchMatch,
  best_frame_thumbnail_url: Uri.nullable().optional(),
});
export type SearchResult = z.infer<typeof SearchResult>;

export const SearchResponseMode = z.enum(['hybrid', 'semantic', 'keyword', 'browse']);
export type SearchResponseMode = z.infer<typeof SearchResponseMode>;

export const SearchResponse = z.object({
  items: z.array(SearchResult),
  next_cursor: z.string().nullable(),
  total_estimate: z.int(),
  facets: Facets,
  applied_hints: z.array(AppliedHint),
  effective_query: z.string().nullable().optional(),
  mode: SearchResponseMode.optional(),
  took_ms: z.int(),
});
export type SearchResponse = z.infer<typeof SearchResponse>;

export const SearchSuggestionKind = z.enum([
  'tag',
  'board',
  'person',
  'object',
  'recent',
  'hint',
  'custom_field_option',
]);
export type SearchSuggestionKind = z.infer<typeof SearchSuggestionKind>;

export const SearchSuggestion = z.object({
  kind: SearchSuggestionKind,
  value: z.string(),
  label: z.string(),
  count: z.int().nullable().optional(),
  thumbnail_url: Uri.nullable().optional(),
  /** Partial `SearchFilters` to merge when chosen. */
  filter_patch: z.record(z.string(), z.unknown()).optional(),
});
export type SearchSuggestion = z.infer<typeof SearchSuggestion>;

export const SavedSearch = z.object({
  id: Id,
  workspace_id: Id,
  user_id: Id,
  name: z.string().max(120),
  request: SearchRequest,
  created_at: Timestamp,
  updated_at: Timestamp.optional(),
});
export type SavedSearch = z.infer<typeof SavedSearch>;

export const SavedSearchCreate = z.object({
  name: z.string().min(1).max(120),
  request: SearchRequest,
});
export type SavedSearchCreate = z.infer<typeof SavedSearchCreate>;
